import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "../observability";
import { getPaidSession, setPaidSession } from "../utils/paidSessions";

/**
 * Stripe payment flows (£1 verdict + £4.99 PDF) — extracted verbatim from
 * App.js (Jun 2026 refactor). Owns the consent-modal → checkout redirect,
 * both payment-status polling loops and the PDF download.
 * Functions are recreated per render (same closure semantics as inline).
 *
 * NOTE: behaviour here is guarded by the Stripe webhook regression history —
 * do not change polling/backoff logic without running the backend pytest suite.
 */
export const usePaymentFlow = ({
  API,
  t,
  language,
  registration,
  targetPrice,
  analysis,
  analysisState,
  consentModal,
  setConsentModal,
  setExpertConfirm,
  setAnalysis,
  setShowVerdict,
  setPdfUnlocked,
  setPaymentLoading,
  setPdfPaymentLoading,
  setPaymentError,
  setError,
  pdfUnlocked,
  pdfPaymentLoading,
  onPaymentConfirmed,
}) => {
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadReport = async (reg, lang, explicitPrice = null) => {
    setDownloadingPdf(true);
    setError("");
    trackEvent('pdf_download_started', { registration: reg, language: lang });
    const priceVal = explicitPrice !== null ? explicitPrice : (targetPrice ? parseFloat(targetPrice) : null);
    // Backend requires a paid pdf-purpose session_id to release the PDF. We
    // look it up from the per-reg paid-session registry; if missing the
    // server returns 402 and we surface the friendly retry error.
    const paidSession = getPaidSession(reg);
    try {
      const response = await axios.post(`${API}/report`, {
        registration: reg,
        language: lang,
        subject_price: priceVal,
        session_id: paidSession?.sessionId || null,
      }, {
        responseType: 'blob',
        timeout: 60000
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Dashed filename per launch-readiness spec — also matches PDF brand handles
      // (no underscores in the user's Downloads list).
      const safeReg = (reg || 'report').toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
      link.setAttribute('download', `worthify-report-${safeReg}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      trackEvent('pdf_downloaded', { registration: reg });
      // Subtle confirmation toast — the browser's native download chrome can
      // be easy to miss on mobile, especially when it slides under the URL bar.
      try {
        toast.success(t('pdfDownloadedToast') || 'Report downloaded', { duration: 3500 });
      } catch { /* sonner may not be mounted */ }
    } catch (e) {
      console.error("Report download error:", e);
      setError('errorReportFailed');
      trackEvent('pdf_download_failed', { registration: reg });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    // ~60s total polling window (30 × 2s). After that we show an explicit
    // error so the user has a clear path: try again, or contact support with
    // the session_id. Stripe rarely takes >10s in practice; the 60s ceiling
    // is for severe network or webhook backlogs.
    const maxAttempts = 30;
    if (attempts >= maxAttempts) {
      analysisState.failPayment(
        (t('paymentTimeoutError') || 'Payment is taking longer than usual. If you were charged, please contact support@worthify.co.uk quoting session ID: ') + sessionId
      );
      return;
    }
    try {
      const response = await fetch(`${API}/payment-status/${sessionId}`);
      const data = await response.json();
      if (data.payment_status === 'paid') {
        trackEvent('payment_completed', { session_id: sessionId });
        // Green "Payment confirmed!" flash in the verification overlay.
        try { onPaymentConfirmed && onPaymentConfirmed(); } catch { /* ignore */ }
        // Persist the verdict-tier session_id so subsequent searches of the
        // SAME registration from this browser stay unlocked (backend gates
        // by session_id, so this just plays the same key back).
        try {
          const verdictReg = analysis?.vehicle?.registration || localStorage.getItem('pendingRegistration');
          if (verdictReg) setPaidSession(verdictReg, sessionId, 'verdict');
        } catch { /* ignore */ }
        // Always fetch the FULL 3-AI consensus result after payment so the
        // user's on-screen verdict matches the PDF. The legacy /analyze
        // endpoint returns the Mongo cache hit instantly (the background
        // task saved the final result ~40-55s after the preliminary). If
        // the cache hasn't been written yet it blocks briefly until it is.
        try {
          const reg = analysis?.vehicle?.registration || localStorage.getItem('pendingRegistration');
          // Persist the asking price to localStorage at submit-time so it survives
          // the Stripe round-trip. targetPrice React state is lost across the
          // external redirect.
          const storedAsking = localStorage.getItem('pendingAskingPrice');
          const priceFromState = targetPrice ? parseFloat(targetPrice) : null;
          const priceRaw = priceFromState
            || (storedAsking ? parseFloat(storedAsking) : null)
            || analysis?.price_check?.subject_price
            || null;
          if (reg) {
            const res = await axios.post(`${API}/analyze`, {
              registration: reg, language: language,
              subject_price: priceRaw, vin: null,
              session_id: sessionId,  // server-side paywall gate
            }, { timeout: 90000 });
            const finalData = res.data;
            if (finalData) {
              setExpertConfirm({ visible: true, from: analysis, to: finalData });
              setShowVerdict(false);
              setPaymentLoading(false);
              return;
            }
          }
        } catch (e) {
          // Fallback: still show expert confirmation overlay with preliminary data
          if (analysis) {
            setExpertConfirm({ visible: true, from: analysis, to: analysis });
            setShowVerdict(false);
            setPaymentLoading(false);
            return;
          }
        }
        localStorage.removeItem('pendingAnalysis');
        localStorage.removeItem('pendingRegistration');
        setShowVerdict(false);
        setPaymentLoading(false);
        return;
      } else if (data.status === 'expired') {
        setPaymentError('Payment session expired. Please try again.');
        setPaymentLoading(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const initiatePayment = async () => {
    // Step 1: open the consent modal. Actual Stripe redirect happens on confirm.
    trackEvent('paywall_consent_modal_opened', { purpose: 'verdict', amount: 1.0 });
    setConsentModal({
      open: true,
      amount: 1.0,
      purpose: "verdict",
      registration: analysis?.vehicle?.registration || registration,
    });
  };

  // Called by PreCheckoutConsentModal after the user ticks the consent box.
  // We record the consent flag server-side (audit trail) and redirect to Stripe.
  // Handles BOTH £1 verdict path and £4.99 PDF path uniformly.
  const performStripeRedirect = async ({ consumer_consent_waived }) => {
    const { purpose, registration: reg } = consentModal;
    const finalReg = reg || analysis?.vehicle?.registration || registration;
    const isPdf = purpose === 'pdf';

    trackEvent('payment_started', {
      purpose: purpose || 'verdict',
      amount: isPdf ? 4.99 : 1.0,
      registration: finalReg,
    });

    // Per-click idempotency key — Stripe will return the SAME Checkout Session
    // for double-tapped CTAs within seconds (common on flaky mobile networks).
    const idempotencyKey = `wf-${finalReg}-${purpose}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    if (isPdf) {
      analysisState.startPdfPayment();
      // Save context for the post-payment return flow.
      try { localStorage.setItem('pendingAnalysis', JSON.stringify(analysis)); } catch (_e) { /* ignore */ }
      try { localStorage.setItem('pendingPdfReg', finalReg); } catch (_e) { /* ignore */ }
      try { localStorage.setItem('pendingPdfLang', language); } catch (_e) { /* ignore */ }
      if (targetPrice) try { localStorage.setItem('pendingPdfPrice', targetPrice); } catch (_e) { /* ignore */ }
    } else {
      analysisState.startPayment();
    }
    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API}/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: originUrl,
          registration: finalReg,
          purpose: purpose || 'verdict',
          consumer_consent_waived: !!consumer_consent_waived,
          idempotency_key: idempotencyKey,
        })
      });
      if (response.status === 429) {
        trackEvent('rate_limit_hit', { endpoint: 'create-checkout', purpose });
        throw new Error('rate_limited');
      }
      const data = await response.json();
      if (!response.ok) {
        // Surface real backend error details (e.g. Stripe key issue, validation)
        const backendMsg = (data && (data.detail || data.message)) || `HTTP ${response.status}`;
        throw new Error(`backend:${backendMsg}`);
      }
      if (data.url) {
        trackEvent('stripe_redirect_initiated', {
          purpose: purpose || 'verdict',
          amount: isPdf ? 4.99 : 1.0,
          session_id: data.session_id,
        });
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (err) {
      setConsentModal((c) => ({ ...c, open: false }));
      const isRateLimit = err?.message === 'rate_limited';
      const isBackend = typeof err?.message === 'string' && err.message.startsWith('backend:');
      const friendlyMsg = isRateLimit
        ? (t('errorRateLimitPayment') || 'Too many payment attempts. Please wait a few minutes and try again.')
        : isBackend
          ? `Payment failed: ${err.message.replace('backend:', '')}`
          : 'Payment failed. Please try again.';
      // Visible toast so the user never wonders "what happened?".
      try { toast.error(friendlyMsg, { duration: 6000 }); } catch { /* sonner may not be mounted yet */ }
      if (isPdf) {
        analysisState.failPdfPayment(friendlyMsg);
        trackEvent('payment_failed', { purpose: 'pdf', stage: 'create_checkout', reason: isRateLimit ? 'rate_limit' : 'unknown' });
      } else {
        analysisState.failPayment(friendlyMsg);
        trackEvent('payment_failed', { purpose: 'verdict', stage: 'create_checkout', reason: isRateLimit ? 'rate_limit' : 'unknown' });
      }
    }
  };

  const pollPdfPaymentStatus = async (sessionId, reg, lang, savedPrice, attempts = 0) => {
    // Resilient polling — exponential backoff (1.5s → 2s → 3s → 5s → 7s → 10s cap)
    // gives Stripe time to propagate the webhook even on a cold replica. Total
    // budget ~90s across 16 attempts. Transient 5xx errors NEVER fail the poll —
    // only an explicit "expired" status or exhausted attempts do.
    const maxAttempts = 16;
    const backoff = [1500, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000];
    const nextDelay = backoff[Math.min(attempts, backoff.length - 1)];
    if (attempts >= maxAttempts) {
      analysisState.failPdfPayment('Payment verification timed out. Please contact support.');
      return;
    }
    try {
      const response = await fetch(`${API}/payment-status/${sessionId}`);
      // Transient HTTP error (Stripe rate limit / backend hiccup) — retry quietly.
      if (!response.ok) {
        setTimeout(() => pollPdfPaymentStatus(sessionId, reg, lang, savedPrice, attempts + 1), nextDelay);
        return;
      }
      const data = await response.json();
      if (data.payment_status === 'paid') {
        // Green "Payment confirmed!" flash in the verification overlay.
        try { onPaymentConfirmed && onPaymentConfirmed(); } catch { /* ignore */ }
        // Persist the paid session_id keyed by REGISTRATION so the buyer's
        // future re-searches of THIS car (and only this car) unlock automatically.
        setPaidSession(reg, sessionId, 'pdf');
        localStorage.removeItem('worthifyPdfUnlockedReg'); // legacy flag cleanup
        // CRITICAL: re-fetch the FULL premium payload from /api/result so
        // `analysis._premium_locked` flips from true → false. Without this,
        // the premium-unlock derivation effect keeps deriving
        // pdfUnlocked=false from the cached £1-tier analysis on every
        // re-render — and the user gets bounced back to the £4.99 paywall
        // (user-reported regression, Feb 2026).
        try {
          const r = await fetch(`${API}/result/${encodeURIComponent(reg)}?language=${encodeURIComponent(lang || 'en')}&session_id=${encodeURIComponent(sessionId)}`);
          if (r.ok) {
            const j = await r.json();
            const fullResult = j?.result;
            if (fullResult && typeof fullResult === 'object') {
              setAnalysis(fullResult);
              // Update cached pendingAnalysis BEFORE we delete it, so any
              // unexpected reload mid-download still has the premium copy.
              try { localStorage.setItem('pendingAnalysis', JSON.stringify(fullResult)); } catch { /* ignore */ }
            }
          }
        } catch (refetchErr) {
          // Network failure here is not fatal — pdfUnlocked below will still
          // unlock Nina + premium modals via the bypass flag. Worst case the
          // user reloads and the next analyze fetches the premium tier via
          // worthifyPaidSessions auth.
          console.warn('Premium re-fetch failed (non-fatal):', refetchErr);
        }
        setPdfUnlocked(true);
        localStorage.removeItem('pendingPdfReg');
        localStorage.removeItem('pendingPdfLang');
        localStorage.removeItem('pendingPdfPrice');
        // pendingAnalysis intentionally kept until after the re-fetch above
        // updates it to the premium tier (or fails). Clean it now.
        localStorage.removeItem('pendingAnalysis');
        setPdfPaymentLoading(false);
        const price = savedPrice ? parseFloat(savedPrice) : null;
        await downloadReport(reg, lang, price);
        return;
      } else if (data.status === 'expired') {
        analysisState.failPdfPayment('Payment session expired. Please try again.');
        return;
      }
      setTimeout(() => pollPdfPaymentStatus(sessionId, reg, lang, savedPrice, attempts + 1), nextDelay);
    } catch {
      setTimeout(() => pollPdfPaymentStatus(sessionId, reg, lang, savedPrice, attempts + 1), nextDelay);
    }
  };

  const initiatePdfPayment = async () => {
    if (pdfPaymentLoading || downloadingPdf) return;

    if (pdfUnlocked) {
      const price = targetPrice ? parseFloat(targetPrice) : null;
      await downloadReport(analysis?.vehicle?.registration || registration, language, price);
      return;
    }

    // Open consent modal — Stripe redirect deferred until user ticks waiver.
    trackEvent('paywall_consent_modal_opened', { purpose: 'pdf', amount: 4.99 });
    setConsentModal({
      open: true,
      amount: 4.99,
      purpose: "pdf",
      registration: analysis?.vehicle?.registration || registration,
    });
  };

  return {
    downloadingPdf,
    downloadReport,
    pollPaymentStatus,
    pollPdfPaymentStatus,
    initiatePayment,
    performStripeRedirect,
    initiatePdfPayment,
  };
};
